import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { Footer } from "@/components/Footer";

const serviceUrl = "https://smilecaredentalgandhinagar.lovable.app/services/root-canal-treatment";
const imageUrl = "https://smilecaredentalgandhinagar.lovable.app/hero-dental.webp";

const rootCanalSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name: "Painless Root Canal Treatment in Gandhinagar",
  alternateName: "Root Canal Treatment Gandhinagar",
  description:
    "Painless root canal treatment in Gandhinagar at Smile Care Dental using rotary endodontics, modern anesthesia, and MDS specialist-led care.",
  bodyLocation: "Tooth pulp",
  procedureType: "TherapeuticProcedure",
  image: imageUrl,
  url: serviceUrl,
  provider: {
    "@type": "Dentist",
    name: "Smile Care Dental",
    url: "https://smilecaredentalgandhinagar.lovable.app/",
    telephone: "+916353963147",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Pramukh Arcade 2, A-212, near Terracotta, opposite NID college",
      addressLocality: "Kudasan",
      addressRegion: "Gujarat",
      postalCode: "382421",
      addressCountry: "IN",
    },
  },
  areaServed: [
    { "@type": "City", name: "Gandhinagar" },
    { "@type": "Place", name: "Kudasan" },
  ],
};

const careSteps = [
  "Clinical examination and digital X-ray to identify the infected tooth canal",
  "Comfort-focused anesthesia before treatment begins",
  "Rotary cleaning and shaping of the root canals",
  "Biocompatible filling to seal the canals and prevent reinfection",
  "Crown or final restoration planning to protect the treated tooth",
];

const benefits = [
  "Relieves tooth pain while preserving your natural tooth",
  "Helps stop infection from spreading to gums or nearby teeth",
  "Modern rotary endodontics improves precision and comfort",
  "MDS specialist-led care for complex dental needs",
];

const RootCanalTreatment = () => {
  return (
    <>
      <Helmet>
        <title>Root Canal Treatment Gandhinagar | Smile Care Dental</title>
        <meta
          name="description"
          content="Painless root canal treatment in Gandhinagar by MDS specialists at Smile Care Dental, Kudasan. Advanced rotary endodontics, modern anesthesia, and tooth-saving care."
        />
        <meta
          name="keywords"
          content="root canal treatment gandhinagar, painless root canal kudasan, dentist for tooth pain gandhinagar, MDS endodontist gandhinagar"
        />
        <link rel="canonical" href={serviceUrl} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Painless Root Canal Treatment in Gandhinagar" />
        <meta
          property="og:description"
          content="Save your natural tooth with comfort-focused root canal treatment by MDS specialists at Smile Care Dental, Kudasan."
        />
        <meta property="og:url" content={serviceUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Root Canal Treatment Gandhinagar | Smile Care Dental" />
        <meta
          name="twitter:description"
          content="Painless root canal treatment in Gandhinagar using rotary endodontics and modern anesthesia."
        />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(rootCanalSchema)}</script>
      </Helmet>

      <Header />
      <main>
        <section className="pt-32 pb-16 bg-background">
          <div className="container mx-auto px-4">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </Link>

            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
              <div>
                <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-5">
                  Pain-Free Endodontics
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-6">
                  Root Canal Treatment in Gandhinagar
                </h1>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
                  Smile Care Dental provides painless root canal treatment in Gandhinagar for patients with deep cavities, tooth infections, swelling, or severe sensitivity. Our MDS specialists use advanced rotary endodontics and modern anesthesia to make treatment predictable, comfortable, and focused on saving your natural tooth.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="hero" size="xl" asChild>
                    <a href="tel:+916353963147">
                      <Phone className="w-5 h-5" />
                      Call 063539 63147
                    </a>
                  </Button>
                  <Button variant="whatsapp" size="xl" asChild>
                    <a
                      href="https://wa.me/916353963147?text=Hi%2C%20I%20need%20root%20canal%20treatment%20in%20Gandhinagar"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Book on WhatsApp
                    </a>
                  </Button>
                </div>
              </div>

              <img
                src="/hero-dental.webp"
                alt="Smile Care Dental clinic for root canal treatment in Gandhinagar"
                width={888}
                height={500}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full aspect-[4/3] object-cover rounded-2xl shadow-card"
              />
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10">
            <article className="bg-card border border-border/50 rounded-2xl p-6 md:p-8 shadow-soft">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">When do you need a root canal?</h2>
              <p className="text-muted-foreground mb-5">
                A root canal may be recommended when the soft tissue inside a tooth becomes inflamed or infected. Common signs include lingering sensitivity to hot or cold, pain while chewing, swelling near the gums, tooth darkening, or a deep cavity. Early diagnosis can often prevent extraction and keep your bite natural.
              </p>
              <p className="text-muted-foreground">
                At our Kudasan clinic, the treatment plan is explained clearly before starting, so you know the expected steps, comfort measures, and restoration options.
              </p>
            </article>

            <article className="bg-card border border-border/50 rounded-2xl p-6 md:p-8 shadow-soft">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Why patients choose Smile Care Dental</h2>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">Our root canal treatment process</h2>
            <div className="grid gap-4">
              {careSteps.map((step, index) => (
                <div key={step} className="flex gap-4 p-5 bg-card border border-border/50 rounded-2xl shadow-soft">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
                    {index + 1}
                  </span>
                  <p className="text-muted-foreground pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Tooth pain should not wait</h2>
            <p className="text-lg text-muted-foreground mb-8">
              If you are searching for root canal treatment Gandhinagar, book a consultation at Smile Care Dental in Kudasan for a careful diagnosis and a comfort-first treatment plan.
            </p>
            <Button variant="hero" size="xl" asChild>
              <a href="tel:+916353963147">
                <Phone className="w-5 h-5" />
                Call for Appointment
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
};

export default RootCanalTreatment;